'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  ConfirmationDialog,
  Field,
  Input,
  LabelDefault,
  MetaDefault,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import type { MergeAssetsPublicBodyDto } from '@terra/shared-contracts-applications';
import {
  ASSET_MERGE_NEW_SURVIVOR_VALUE,
  getAssetMergePath,
  isAssetMergeConfirmReady,
  isAssetMergeReviseSelection,
  type AssetMergeCandidate,
} from '../../utils/asset-merge/asset-merge-selection';
import { buildMergeAssetsPayload } from '../../utils/asset-merge/build-merge-assets-payload';
import { suggestAssetMergeSurvivor } from '../../utils/asset-merge/suggestAssetMergeSurvivor';
import { AssetMergeSurvivorList } from './AssetMergeSurvivorList';

type DialogChoice = { kind: 'EXISTING'; id: string } | { kind: 'NEW' };

type AssetMergeConfirmDialogProps = {
  isOpen: boolean;
  assets: readonly AssetMergeCandidate[];
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (body: MergeAssetsPublicBodyDto) => void | Promise<void>;
};

export function AssetMergeConfirmDialog({
  isOpen,
  assets,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: AssetMergeConfirmDialogProps) {
  const t = useTranslations('proxy.assets.merge');
  const tCommon = useTranslations('common');
  const isRevise = isAssetMergeReviseSelection(assets);
  const suggestion = useMemo(
    () => suggestAssetMergeSurvivor({ assets }),
    [assets],
  );
  const [choice, setChoice] = useState<DialogChoice | undefined>(undefined);
  const [newPath, setNewPath] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setChoice(
      isRevise
        ? { kind: 'NEW' }
        : suggestion.status === 'existing'
          ? { kind: 'EXISTING', id: suggestion.id }
          : undefined,
    );
    setNewPath(assets[0] === undefined ? '' : getAssetMergePath(assets[0]));
  }, [assets, isOpen, isRevise, suggestion]);

  const selectedValue =
    choice?.kind === 'EXISTING'
      ? choice.id
      : choice?.kind === 'NEW'
        ? ASSET_MERGE_NEW_SURVIVOR_VALUE
        : '';
  const canConfirm = isAssetMergeConfirmReady({ choice, newPath, assets });
  const showPathField = isRevise || choice?.kind === 'NEW';

  const handleValueChange = (value: string) => {
    if (value === ASSET_MERGE_NEW_SURVIVOR_VALUE) {
      setChoice({ kind: 'NEW' });

      return;
    }

    if (value.length > 0) {
      setChoice({ kind: 'EXISTING', id: value });
    }
  };

  const handleConfirm = async () => {
    if (choice === undefined || !canConfirm) {
      return;
    }

    await onConfirm(
      buildMergeAssetsPayload({
        assets,
        choice:
          choice.kind === 'NEW'
            ? { kind: 'NEW', path: newPath.trim() }
            : choice,
      }),
    );
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onOpenChange={({ open }) => onOpenChange(open)}
      onConfirm={handleConfirm}
      loading={isSubmitting}
      variant="warning"
      title={isRevise ? t('reviseTitle') : t('title')}
      description={
        isRevise ? '' : suggestion.status === 'none' ? t('noWinner') : ''
      }
      confirmText={tCommon('confirm')}
      cancelText={tCommon('cancel')}
      confirmDisabled={!canConfirm}
      maxWidth="560px"
      indentChildren={false}
    >
      <VStack gap="3" alignItems="stretch" w="full">
        {isRevise ? null : (
          <AssetMergeSurvivorList
            assets={assets}
            suggestedId={
              suggestion.status === 'existing' ? suggestion.id : undefined
            }
            selectedValue={selectedValue}
            onSelectValue={handleValueChange}
          />
        )}

        {showPathField ? (
          <Field.Root>
            <Field.Label>
              <LabelDefault>{t('pathLabel')}</LabelDefault>
            </Field.Label>
            <Input
              size="sm"
              value={newPath}
              onChange={(event) => setNewPath(event.target.value)}
            />
            {isRevise ? (
              <Field.HelperText>
                <MetaDefault color="fg.muted">
                  {t('revisePathHint')}
                </MetaDefault>
              </Field.HelperText>
            ) : null}
          </Field.Root>
        ) : null}
      </VStack>
    </ConfirmationDialog>
  );
}
